import { ApolloClient, gql, useApolloClient } from '@apollo/client';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useHeaderInject } from '../../../../containers/file-entities-lists/components/contentWrapper/HeaderComponentProvider';
import {
  parseTableSettings,
  stringifyTableSettings,
} from '../../make-table-settings';

type ErpHeaderValues = 'receipts' | 'positions';

interface ErpPageHeaderProps {
  value: ErpHeaderValues;
  onChange: (value: ErpHeaderValues) => void;
  disablePosition?: boolean;
}

export const ErpPageHeader = (props: ErpPageHeaderProps) => {
  const { value, onChange, disablePosition } = props;
  const { t } = useTranslation();
  const handleChange = useCallback(
    (_e, value) => {
      onChange(value);
    },
    [onChange]
  );
  return (
    <div style={{ paddingRight: '1rem' }}>
      <Tabs
        value={value}
        onChange={handleChange}
        TabIndicatorProps={{ hidden: true }}
        variant="scrollable"
      >
        <Tab label={t('COMMON.TRAVELRECEIPTS')} value="receipts"></Tab>
        <Tab
          label={t('MASK.POSITIONS')}
          value="positions"
          disabled={disablePosition}
        ></Tab>
      </Tabs>
    </div>
  );
};

export const useErpHeader = (
  entity: Entities,
  currentValue: ErpHeaderValues,
  disablePosition?: boolean
) => {
  const navigate = useNavigate();
  const client = useApolloClient();

  useHeaderInject(
    useMemo(
      () => (
        <ErpPageHeader
          value={currentValue}
          disablePosition={disablePosition}
          onChange={async (value) => {
            // Here we map settings from one table to another - for now is only single property
            let variantUrl = '';
            if (value === 'positions') {
              const search = getPositionSearch();
              variantUrl = `position(${entity})` + (search ? `?${search}` : '');
            } else {
              const search = await getErpSearch(client);
              variantUrl = `${entity}` + (search ? `?${search}` : '');
            }

            navigate(`/more/entity/${variantUrl}`, {
              replace: true,
            });
          }}
        />
      ),
      [client, currentValue, disablePosition, entity, navigate]
    )
  );
};

const getPositionSearch = () => {
  const settings = new URLSearchParams(window.location.search).get('settings');
  const settingsObject = settings ? parseTableSettings(settings) : undefined;
  const filters = settingsObject?.filter?.filter((x) =>
    [
      'businessPartnerContactCombined.businessPartner.data.id',
      'date',
      'contractDate',
      'user.id',
    ].includes(x.id)
  );

  let search = '';
  const newParams = new URLSearchParams();

  const resultingFilter = [];
  if (filters)
    for (const filter of filters) {
      switch (filter.id) {
        case 'businessPartnerContactCombined.businessPartner.data.id':
          if (Array.isArray(filter.value)) {
            resultingFilter.push({
              id: 'details.bz_Company_Name',
              value: filter.value.map((x) => x.name),
            });
          }
          break;
        case 'user.id':
          if (Array.isArray(filter.value)) {
            resultingFilter.push({
              id: 'details.bz_UserResponsible',
              value: filter.value.map((x) => x.displayName),
            });
          }
          break;
        case 'date':
        case 'contractDate':
          if (filter.value) {
            resultingFilter.push({
              id: 'details.bz_Date',
              value: filter.value,
            });
          }
          break;
      }
    }

  newParams.set(
    'settings',
    stringifyTableSettings({
      filter: resultingFilter,
    })
  );
  search = newParams.toString();
  return search;
};

const getErpSearch = async (client: ApolloClient<object>) => {
  const settings = new URLSearchParams(window.location.search).get('settings');
  const settingsObject = settings ? parseTableSettings(settings) : undefined;
  const filters = settingsObject?.filter?.filter((x) =>
    [
      'details.bz_Company_Name',
      'details.bz_UserResponsible',
      'details.bz_Date',
    ].includes(x.id)
  );

  const newParams = new URLSearchParams();
  const resultingFilter = [];
  let search = '';

  if (filters)
    for (const filter of filters) {
      switch (filter.id) {
        case 'details.bz_Company_Name': {
          const bpResponse = await client.query({
            fetchPolicy: 'no-cache',
            query: BP_QUERY,
            variables: {
              filter: JSON.stringify([{ name: { $in: filter.value } }]),
            },
          });
          const bpData = Object.values(bpResponse.data).flatMap(
            (x: { data: { id: number; name: string }[] }) => x.data
          );

          resultingFilter.push({
            id: 'businessPartnerContactCombined.businessPartner.data.id',
            value: bpData,
          });
          break;
        }
        case 'details.bz_UserResponsible': {
          const userResponse = await client.query({
            fetchPolicy: 'no-cache',
            query: USER_QUERY,
            variables: {
              filter: JSON.stringify([{ anzeigename: { $in: filter.value } }]),
            },
          });
          const userData = Object.values(userResponse.data).flatMap(
            (x: { data: { id: number; displayName: string }[] }) => x.data
          );

          resultingFilter.push({
            id: 'user.id',
            value: userData,
          });
          break;
        }
        case 'details.bz_Date': {
          if (filter.value) {
            resultingFilter.push({
              id: 'date',
              value: filter.value,
            });
            resultingFilter.push({
              id: 'contractDate',
              value: filter.value,
            });
          }
          break;
        }
      }
    }

  newParams.set(
    'settings',
    stringifyTableSettings({
      filter: resultingFilter,
    })
  );

  search = newParams.toString();
  return search;
};

const BP_QUERY = gql`
  query getKundenUndLieferanten(
    $categoryCodes: [Int]
    $querySize: Int
    $queryPage: Int
    $querySortOrder: SortOrderStyle
    $querySortBy: String
    $querySortByEx: [SortByPair]
    $filter: String
    $addFavSort: Boolean
    $onlyFavItems: Boolean
  ) {
    getKunden: getKunden(
      categoryCodes: $categoryCodes
      querySize: $querySize
      queryPage: $queryPage
      querySortOrder: $querySortOrder
      querySortBy: $querySortBy
      querySortByEx: $querySortByEx
      filter: $filter
      addFavSort: $addFavSort
      onlyFavItems: $onlyFavItems
    ) {
      total
      size
      page
      data {
        id: code
        name: name
        __typename
      }
      __typename
    }

    getLieferanten: getLieferanten(
      categoryCodes: $categoryCodes
      querySize: $querySize
      queryPage: $queryPage
      querySortOrder: $querySortOrder
      querySortBy: $querySortBy
      querySortByEx: $querySortByEx
      filter: $filter
      addFavSort: $addFavSort
      onlyFavItems: $onlyFavItems
    ) {
      total
      size
      page
      data {
        id: code
        name: name
        __typename
      }
      __typename
    }
  }
`;

const USER_QUERY = gql`
  query getBenutzer(
    $querySize: Int
    $queryPage: Int
    $querySortOrder: SortOrderStyle
    $querySortBy: String
    $querySortByEx: [SortByPair]
    $filter: String
  ) {
    getBenutzer(
      querySize: $querySize
      queryPage: $queryPage
      querySortOrder: $querySortOrder
      querySortBy: $querySortBy
      querySortByEx: $querySortByEx
      filter: $filter
    ) {
      total
      size
      page
      sortBy
      sortOrder
      data {
        displayName: anzeigename
        id: code
      }
    }
  }
`;
